import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import { useConfirm } from '../shared/Confirm';
import { useIsMobile } from '../../utils/useIsMobile';

// Datos de ejemplo para la vista previa. No salen de la base: solo sirven
// para que se vea como quedara el mensaje antes de guardarlo.
const EJEMPLO = {
  cliente:          'María Gómez',
  negocio:          'tu negocio',
  servicio:         'Corte y barba',
  barbero:          'Duvan Lopez',
  fecha:            '22/08/2026',
  hora:             '3:00 p.m.',
  enlace:           'https://ahasolutionsas.com/book/tu-negocio',
  antelacion:       '3 horas',
  telefono_cliente: '573001234567',
};

/**
 * Misma sustitucion que hace el servidor: una linea cuyos marcadores vienen
 * todos vacios se elimina entera. Aqui se repite para que la vista previa
 * coincida con lo que recibira el cliente.
 */
function render(cuerpo, datos) {
  return String(cuerpo || '')
    .split('\n')
    .filter(linea => {
      const marcadores = linea.match(/\{\{(\w+)\}\}/g);
      if (!marcadores) return true;
      return marcadores.some(m => {
        const v = datos[m.slice(2, -2)];
        return v !== undefined && v !== null && String(v).trim() !== '';
      });
    })
    .join('\n')
    .replace(/\{\{(\w+)\}\}/g, (_, c) => (datos[c] == null ? '' : String(datos[c])))
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default function MessageTemplates({ negocioId, nombreNegocio }) {
  const esMovil = useIsMobile(900);

  const [plantillas, setPlantillas] = useState([]);
  const [campos, setCampos]   = useState({});
  const [activa, setActiva]   = useState(null);   // tipo seleccionado
  const [texto, setTexto]     = useState('');
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso]     = useState('');
  const [error, setError]     = useState('');
  const areaRef = useRef(null);
  const confirmar = useConfirm();

  const notificar = (msg, esError = false) => {
    if (esError) { setError(msg); setTimeout(() => setError(''), 5000); }
    else         { setAviso(msg); setTimeout(() => setAviso(''), 4000); }
  };

  const cargar = useCallback(async () => {
    if (!negocioId) return;
    setCargando(true);
    try {
      const r = await api.get(`/businesses/${negocioId}/templates`);
      setPlantillas(r.data.plantillas || []);
      setCampos(r.data.campos || {});
      setActiva(a => a || r.data.plantillas?.[0]?.tipo || null);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar los mensajes');
    } finally { setCargando(false); }
  }, [negocioId]);

  useEffect(() => { cargar(); }, [cargar]);

  const actual = plantillas.find(p => p.tipo === activa) || null;

  // Al cambiar de mensaje se carga su texto en el editor.
  useEffect(() => { if (actual) setTexto(actual.texto); }, [actual?.tipo]); // eslint-disable-line

  const sinGuardar = actual && texto !== actual.texto;

  const elegir = async (tipo) => {
    if (sinGuardar && !await confirmar({
      titulo: 'Cambios sin guardar',
      mensaje: 'Tienes cambios sin guardar en este mensaje.',
      detalle: 'Si continúas se perderán.',
      aceptar: 'Descartar cambios',
      cancelar: 'Seguir editando',
      peligro: true,
    })) return;
    setActiva(tipo);
  };

  const insertar = (clave) => {
    const marcador = `{{${clave}}}`;
    const area = areaRef.current;
    if (!area) { setTexto(t => t + marcador); return; }
    // Se inserta donde esta el cursor, no al final: si no, hay que cortar y
    // pegar a mano cada vez.
    const ini = area.selectionStart ?? texto.length;
    const fin = area.selectionEnd ?? texto.length;
    const nuevo = texto.slice(0, ini) + marcador + texto.slice(fin);
    setTexto(nuevo);
    requestAnimationFrame(() => {
      area.focus();
      area.setSelectionRange(ini + marcador.length, ini + marcador.length);
    });
  };

  const guardar = async () => {
    if (!actual) return;
    if (!texto.trim()) return notificar('El mensaje no puede quedar vacío. Usa «Restaurar original» si quieres volver al texto de fábrica.', true);
    setGuardando(true);
    try {
      const r = await api.put(`/businesses/${negocioId}/templates`, { tipo: actual.tipo, cuerpo: texto });
      setPlantillas(ps => ps.map(p => p.tipo === actual.tipo
        ? { ...p, texto: r.data.texto, personalizada: r.data.personalizada } : p));
      setTexto(r.data.texto);
      notificar(r.data.message);
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo guardar el mensaje', true);
    } finally { setGuardando(false); }
  };

  const restaurar = async () => {
    if (!actual) return;
    if (!await confirmar({
      titulo: 'Restaurar mensaje original',
      mensaje: 'Se volverá al mensaje original de la plataforma.',
      detalle: 'Se perderá el texto que hayas personalizado para este negocio.',
      aceptar: 'Restaurar',
      peligro: true,
    })) return;
    setGuardando(true);
    try {
      const r = await api.put(`/businesses/${negocioId}/templates`, { tipo: actual.tipo, cuerpo: '' });
      setPlantillas(ps => ps.map(p => p.tipo === actual.tipo
        ? { ...p, texto: r.data.texto, personalizada: false } : p));
      setTexto(r.data.texto);
      notificar(r.data.message);
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo restaurar el mensaje', true);
    } finally { setGuardando(false); }
  };

  if (cargando) {
    return <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando mensajes...</div>;
  }

  const datosVista = { ...EJEMPLO, negocio: nombreNegocio || EJEMPLO.negocio };

  return (
    <>
      {aviso && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{aviso}</div>}
      {error && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      <div className="card" style={{ padding: '0.9rem 1.1rem', marginBottom: '1rem', fontSize: '0.85rem', lineHeight: 1.6 }}>
        Estos son los mensajes de WhatsApp que salen desde el número de tu negocio.
        Escríbelos como quieras y usa los <strong>campos</strong> de la derecha para
        que cada mensaje llegue con los datos reales de cada cita.
      </div>

      {/* Selector de mensaje */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '1rem' }}>
        {plantillas.map(p => {
          const sel = p.tipo === activa;
          return (
            <button key={p.tipo} onClick={() => elegir(p.tipo)} style={{
              padding: '7px 13px', borderRadius: 8, cursor: 'pointer', fontSize: '0.83rem',
              fontFamily: 'var(--font-body)', fontWeight: sel ? 700 : 500,
              border: `1px solid ${sel ? 'var(--color-gold)' : 'var(--color-border)'}`,
              background: sel ? 'var(--color-gold-subtle)' : 'transparent',
              color: sel ? 'var(--color-gold)' : 'var(--color-text-muted)',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {p.titulo}
              {p.personalizada && (
                <span title="Este mensaje está personalizado" style={{ fontSize: '0.7rem' }}>✎</span>
              )}
            </button>
          );
        })}
      </div>

      {actual && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: esMovil ? '1fr' : 'minmax(0,1fr) 280px',
          gap: '1.25rem', alignItems: 'start',
        }}>
          {/* Editor y vista previa */}
          <div style={{ minWidth: 0 }}>
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{actual.titulo}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  {actual.cuando} Va dirigido a: <strong>{actual.destinatario}</strong>.
                </div>
              </div>

              <textarea
                ref={areaRef}
                className="form-input"
                rows={12}
                value={texto}
                onChange={e => setTexto(e.target.value)}
                style={{ fontFamily: 'inherit', lineHeight: 1.6, resize: 'vertical' }}
              />

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
                <button className="btn btn-primary" onClick={guardar} disabled={guardando || !sinGuardar}>
                  {guardando ? 'Guardando...' : 'Guardar mensaje'}
                </button>
                {actual.personalizada && (
                  <button className="btn btn-ghost" onClick={restaurar} disabled={guardando}>
                    ↺ Restaurar original
                  </button>
                )}
                {sinGuardar && (
                  <span style={{ fontSize: '0.78rem', color: 'var(--color-warning)' }}>Sin guardar</span>
                )}
              </div>
            </div>

            <div className="card">
              <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>Vista previa</div>
              <div style={{
                background: 'var(--color-surface-3)', borderRadius: 10, padding: '0.9rem 1rem',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                fontSize: '0.85rem', lineHeight: 1.6, maxWidth: 420,
              }}>{render(texto, datosVista) || <span style={{ color: 'var(--color-text-muted)' }}>El mensaje está vacío</span>}</div>
              <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '8px 0 0' }}>
                Con datos de ejemplo. Los asteriscos ponen el texto en <strong>negrita</strong> dentro de WhatsApp,
                y el guion bajo lo pone en <em>cursiva</em>.
              </p>
            </div>
          </div>

          {/* Campos disponibles */}
          <div className="card" style={{ alignSelf: 'start' }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 4 }}>Campos disponibles</div>
            <p style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)', margin: '0 0 12px' }}>
              Pulsa uno para insertarlo donde tengas el cursor. Solo estos funcionan
              en este mensaje.
            </p>

            {actual.campos.map(clave => {
              const c = campos[clave] || { etiqueta: `{{${clave}}}`, descripcion: '' };
              return (
                <button
                  key={clave}
                  onClick={() => insertar(clave)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                    background: 'none', border: 'none', borderBottom: '1px solid var(--color-border)',
                    padding: '8px 2px', fontFamily: 'var(--font-body)',
                  }}
                >
                  <code style={{
                    fontSize: '0.76rem', color: 'var(--color-gold)', fontWeight: 600,
                  }}>{c.etiqueta}</code>
                  <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', lineHeight: 1.45 }}>
                    {c.descripcion}
                  </div>
                </button>
              );
            })}

            <p style={{ fontSize: '0.73rem', color: 'var(--color-text-muted)', margin: '12px 0 0', lineHeight: 1.5 }}>
              Si un campo llega vacío —por ejemplo, una cita sin barbero asignado— esa
              línea del mensaje no se envía.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
